import { useSyncExternalStore } from 'react';

// One-way quality ratchet shared app-wide: ONE rAF sampler (started by the first subscriber,
// stopped by the last). If the median frame time over a ~2s window exceeds budget, step down
// once and never climb back this session (can't oscillate).
const BUDGET_MS = 1000 / 50;
const WINDOW = 120;
const MAX_STEP = 2;
const WARMUP_MS = 1500; // ignore mount jank (shader compile, texture upload, chunk load)

export function shouldStepDown(samples: number[], budget: number): boolean {
  if (samples.length === 0) return false;
  const sorted = [...samples].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] > budget;
}

let sessionStep = 0;
const listeners = new Set<() => void>();
let raf = 0;
let samples: number[] = [];
let last = 0;
let warmUntil = 0;

function tick(t: number) {
  raf = 0;
  if (t >= warmUntil && !document.hidden) samples.push(t - last);
  last = t;
  if (samples.length >= WINDOW) {
    const down = shouldStepDown(samples, BUDGET_MS);
    samples = [];
    if (down && sessionStep < MAX_STEP) { sessionStep++; listeners.forEach((l) => l()); }
  }
  if (sessionStep < MAX_STEP && listeners.size > 0) raf = requestAnimationFrame(tick);
}

function restartWindow() {
  samples = [];
  last = performance.now();
  warmUntil = last + WARMUP_MS;
}

function subscribe(l: () => void) {
  listeners.add(l);
  restartWindow(); // new subscriber => its mount jank must not count
  if (!raf && sessionStep < MAX_STEP) raf = requestAnimationFrame(tick);
  return () => {
    listeners.delete(l);
    if (listeners.size === 0 && raf) { cancelAnimationFrame(raf); raf = 0; }
  };
}

const getSnapshot = () => sessionStep;

export function useFpsBackoff(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
